// CreatorProExplainer (formerly AffiliateExplainer) — reusable modal that
// explains the Creator Pro upgrade. PRODUCT-FIRST (Jul 2026): sells the
// toolkit, not an income opportunity. The Partner Program is a quiet note
// with the earnings disclaimer. Triggered from share/upsell CTAs.
//
// Contract unchanged: { open, onClose, onUpgrade }.

import { motion } from "framer-motion";
import { Crown, X, Sparkle, Lightning, Trophy, ArrowRight, Users } from "@phosphor-icons/react";
import EarningsDisclaimer from "@/components/EarningsDisclaimer";
import PartnerProgramNote from "@/components/PartnerProgramNote";

export default function AffiliateExplainer({ open, onClose, onUpgrade }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" data-testid="affiliate-explainer-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-7 relative max-h-[90vh] overflow-y-auto"
      >
        <button onClick={onClose} className="absolute top-3 right-3 text-[#6B6A68] hover:text-[#1C1B1A]" data-testid="affiliate-explainer-close">
          <X size={18} />
        </button>

        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C16E5A] to-[#A85E4A] text-white flex items-center justify-center shrink-0">
            <Crown size={22} weight="duotone" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C16E5A]">Creator Pro</div>
            <h2 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">What's Creator Pro?</h2>
          </div>
        </div>

        <p className="text-sm text-[#1C1B1A] mt-4 leading-relaxed">
          <strong>Creator Pro</strong> is a one-time upgrade that unlocks the full My Silo toolkit on top of your
          Creator subscription — the advanced tools for building, growing, and automating your Digital AI Twin.
        </p>

        <div className="mt-5 space-y-3">
          <div className="flex items-start gap-3 bg-[#FDFCF8] border hairline rounded-md p-3" data-testid="explainer-perk-twin">
            <Sparkle size={16} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-[#1C1B1A]">More silos + Boost</div>
              <div className="text-xs text-[#6B6A68]">Grow past the 3-silo limit and turn on deeper, longer, higher-fidelity AI answers.</div>
            </div>
          </div>
          <div className="flex items-start gap-3 bg-[#FDFCF8] border hairline rounded-md p-3" data-testid="explainer-perk-monetize">
            <Lightning size={16} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-[#1C1B1A]">Macros + YouTube Digital Twin Kit</div>
              <div className="text-xs text-[#6B6A68]">Automate ingestion and turn a whole channel into a talking twin, hands-free.</div>
            </div>
          </div>
          <div className="flex items-start gap-3 bg-[#FDFCF8] border hairline rounded-md p-3" data-testid="explainer-perk-referrals">
            <Trophy size={16} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-[#1C1B1A]">Silo Wars, Collaboration & AI Assistant</div>
              <div className="text-xs text-[#6B6A68]">Compete and rank up, invite teammates into a silo, and get a build-side co-pilot.</div>
            </div>
          </div>
        </div>

        <div className="mt-5 bg-[#F0F1EE] border-l-4 border-[#4A5D4E] rounded-r-md p-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#4A5D4E] mb-1.5">
            <Users size={12} weight="duotone" /> Partner Program
          </div>
          <p className="text-xs text-[#1C1B1A] leading-relaxed">
            <PartnerProgramNote testId="explainer-partner-program-note" />
          </p>
          <div className="mt-2"><EarningsDisclaimer variant="short" /></div>
        </div>

        <div className="mt-6 flex flex-col gap-2">
          <button
            onClick={onUpgrade}
            data-testid="explainer-upgrade-btn"
            className="w-full bg-[#C16E5A] text-white hover:bg-[#A85E4A] px-5 py-3 rounded-md flex items-center justify-center gap-2 text-sm font-medium"
          >
            Unlock Creator Pro <ArrowRight size={14} />
          </button>
          <button
            onClick={onClose}
            data-testid="explainer-later-btn"
            className="w-full text-xs text-[#6B6A68] hover:text-[#1C1B1A] py-2"
          >
            Maybe later
          </button>
        </div>
      </motion.div>
    </div>
  );
}
